// iter540/iter566 — Extracted from BankStatementsPage.jsx
// Renders the "Monthly reconciliation progress" card: a per-month
// grid of mini progress bars + expandable "top unlinked" rows.

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CalendarCheck2, ChevronDown, ChevronRight, CheckCircle2, Clock, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/stores';

function statusMetaFor(status) {
  if (status === 'closed') return { label: 'Closed', color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' };
  if (status === 'near_closed' || status === 'nearly_closed') return { label: 'Near closed', color: 'bg-sky-500', text: 'text-sky-700', bg: 'bg-sky-50 border-sky-200', dot: 'bg-sky-500' };
  if (status === 'in_progress') return { label: 'In progress', color: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500' };
  return { label: 'Open', color: 'bg-amber-500', text: 'text-amber-800', bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500' };
}

export function MonthlyReconciliationCard({ monthly, expandedYm, onToggle, onOpenStatement, fmt0, onLockChanged }) {
  const user = useAuthStore(s => s.user);
  const canLock = user?.role === 'admin' || user?.is_super_admin;

  const lockMonth = async (ym, locked) => {
    if (!canLock) { toast.error('Only admins / super admins can lock months.'); return; }
    const action = locked ? 'reopen' : 'close';
    const label = locked ? 'Unlock' : 'Lock';
    if (!window.confirm(`${label} ${ym}? ${locked ? 'Edits to journal entries / expenses in this period will be allowed again.' : 'No further edits to journal entries / expenses in this period will be possible until you unlock it.'}`)) return;
    try {
      await api.post(`/monthly-recon/${action}`, { month: ym });
      toast.success(`${ym} ${locked ? 'unlocked' : 'locked'}`);
      onLockChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    }
  };

  if (!monthly || !monthly.months || monthly.months.length === 0) return null;
  return (
    <Card className="border-border" data-testid="monthly-tracker-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-outfit text-base flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            <CalendarCheck2 size={18} className="text-emerald-600" />
            Monthly reconciliation progress
            <Badge variant="secondary" className="font-normal">{monthly.months.length} months</Badge>
          </span>
          <span className="text-[11px] font-normal text-muted-foreground">
            Closed only at 100% · Near-closed ≥ 95% · In-progress ≥ 80%
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5" data-testid="monthly-tracker-grid">
          {monthly.months.map((m) => {
            const expanded = expandedYm === m.ym;
            const pct = Math.round(m.completion_pct || 0);
            const s = statusMetaFor(m.status);
            const [yy, mm] = (m.ym || '').split('-');
            const monthLabel = yy && mm
              ? new Date(parseInt(yy), parseInt(mm) - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' })
              : m.ym;
            return (
              <div
                key={m.ym}
                className={`rounded-xl border p-3 transition-all ${m.locked ? 'bg-rose-50 border-rose-200 ring-1 ring-rose-200/60' : s.bg} ${expanded ? 'ring-2 ring-primary/30 shadow' : ''}`}
                data-testid={`monthly-row-${m.ym}`}
              >
                <button
                  type="button"
                  onClick={() => onToggle(expanded ? null : m.ym)}
                  className="w-full flex items-center justify-between gap-2 text-left"
                  data-testid={`monthly-toggle-${m.ym}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {expanded ? <ChevronDown size={14} className="text-muted-foreground shrink-0" /> : <ChevronRight size={14} className="text-muted-foreground shrink-0" />}
                    <span className="text-sm font-semibold text-foreground truncate">{monthLabel}</span>
                    <span className={`h-1.5 w-1.5 rounded-full ${m.locked ? 'bg-rose-500' : s.dot} shrink-0`} />
                    <span className={`text-[10px] uppercase tracking-wide font-bold shrink-0 ${m.locked ? 'text-rose-700' : s.text}`}>{m.locked ? 'Locked' : s.label}</span>
                  </div>
                  <span className="text-sm font-bold tabular-nums" data-testid={`monthly-pct-${m.ym}`}>{pct}%</span>
                </button>
                <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div className={`h-full ${m.locked ? 'bg-rose-500' : s.color} transition-all`} style={{ width: `${Math.max(2, pct)}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
                  <span>{m.linked_txns}/{m.total_txns} linked</span>
                  <span className="flex items-center gap-2">
                    <span className="text-success">↑ {fmt0(m.total_in)}</span>
                    <span className="text-error">↓ {fmt0(m.total_out)}</span>
                  </span>
                </div>
                {/* iter711 — Lock/unlock controls. Lock badge with closer info renders inline; admin gets the toggle button. */}
                {m.locked && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px] bg-rose-100/70 rounded-md px-2 py-1.5 border border-rose-200" data-testid={`lock-banner-${m.ym}`}>
                    <span className="inline-flex items-center gap-1 font-medium text-rose-800">
                      <Lock size={11} /> Locked by {m.locked_by || 'admin'}
                      {m.locked_at && <span className="text-rose-600/70">· {String(m.locked_at).slice(0,10)}</span>}
                    </span>
                    {canLock && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); lockMonth(m.ym, true); }}
                        className="text-[10px] uppercase tracking-wide font-bold text-rose-700 hover:text-rose-900"
                        data-testid={`unlock-btn-${m.ym}`}
                      >
                        Unlock
                      </button>
                    )}
                  </div>
                )}
                {!m.locked && canLock && pct >= 95 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); lockMonth(m.ym, false); }}
                      className="w-full text-[10px] uppercase tracking-wide font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center justify-center gap-1 bg-white/70 border border-emerald-200 rounded-md px-2 py-1"
                      data-testid={`lock-btn-${m.ym}`}
                    >
                      <Lock size={10} /> Lock this month
                    </button>
                  </div>
                )}
                {expanded && (
                  <div className="mt-3 pt-3 border-t border-border/60 space-y-1.5" data-testid={`monthly-unlinked-${m.ym}`}>
                    {m.unlinked_txns === 0 ? (
                      <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 font-medium">
                        <CheckCircle2 size={12} /> All transactions linked — nothing to review.
                      </div>
                    ) : (
                      <>
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                          Top {Math.min(5, m.unlinked_txns)} unlinked · {m.unlinked_txns} total
                        </div>
                        {(m.top_unlinked || []).map((u, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => onOpenStatement(u.statement_id)}
                            className="w-full text-left flex items-start gap-2 p-1.5 rounded-md hover:bg-muted/60 transition-colors"
                            data-testid={`monthly-unlinked-row-${m.ym}-${i}`}
                            title="Open this statement"
                          >
                            <Clock size={11} className="text-muted-foreground mt-0.5 shrink-0" />
                            <span className="flex-1 min-w-0 text-[11px] text-foreground truncate">{u.description || '—'}</span>
                            <span className={`text-[11px] font-mono font-bold shrink-0 ${u.debit > 0 ? 'text-error' : 'text-success'}`}>
                              {fmt0(u.amount)}
                            </span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
